import type { UseFetchOptions, UseFetchReturn } from "@vueuse/core";
import { computed, type MaybeRef, type MaybeRefOrGetter, toValue } from "vue";

import type { SubmitReplyParams } from "@/composition/api/messagesApi";
import type { DpaNewsMessageAuthor } from "@/types/messenger/comments";
import type {
  DpaNewsMessageItem,
  DpaNewsMessageReply,
  DpaNewsTopicItem,
} from "@/types/messenger/messages";
import { messengerApiClient } from "@/utils/apiClients.ts";

function useNewTopic(
  newMessage: MaybeRef<{
    messageBody: string;
    messageAuthor: DpaNewsMessageAuthor;
  }>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsMessageItem> {
  return messengerApiClient("", {
    ...options,
    beforeFetch({ url, options }) {
      const trimmedUrl = url.replace(/\/$/, "");
      return {
        url: trimmedUrl,
        options,
      };
    },
  })
    .post(() => {
      const { messageBody, messageAuthor } = toValue(newMessage);
      return {
        messageBody,
        ...messageAuthor,
      };
    })
    .json();
}

function useAllMessages(
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsTopicItem[]> {
  return messengerApiClient("", {
    ...options,
    beforeFetch({ url, options }) {
      const trimmedUrl = url.replace(/\/$/, "");
      return {
        url: trimmedUrl,
        options,
      };
    },
    afterFetch(ctx) {
      return {
        ...ctx,
        data: ctx.data.map((topic: DpaNewsTopicItem) => {
          return {
            ...topic,
            isNew: false,
          };
        }),
      };
    },
  }).json();
}

function useTopicReply(
  reply: MaybeRef<SubmitReplyParams>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsMessageReply> {
  const url = computed(() => `${toValue(reply)?.topicMessageId}/replies`);
  return messengerApiClient(url, options)
    .post(() => {
      const { replyBody, messageAuthor, topicMessageId } = toValue(reply);
      return {
        replyBody,
        topicMessageId,
        ...messageAuthor,
      };
    })
    .json();
}

// GET a specific message by ID
function useMessages(
  messageId: MaybeRefOrGetter<string>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<DpaNewsTopicItem> {
  return messengerApiClient(messageId, options).json();
}

export { useAllMessages, useMessages, useNewTopic, useTopicReply };
